/**
 * GDPR Compliant Lazy Loader
 * Manages third-party script loading based on user consent
 * Improves INP by reducing initial JavaScript load
 */

class GDPRLazyLoader {
  constructor() {
    this.consentGiven = false;
    this.consentTypes = {
      analytics: false,
      marketing: false,
      functional: false,
      necessary: true // Always true
    };
    
    this.scriptQueue = [];
    this.loadedScripts = new Set();
    this.debug = window.location.search.includes('debug=gdpr');
    
    this.init();
  }

  init() {
    this.checkExistingConsent();
    this.setupConsentListener();
    this.setupDeferredLoading();
    
    if (this.debug) {
      console.log('🔒 GDPR Lazy Loader initialized');
    }
  }

  /**
   * Check for existing consent from localStorage or cookies
   */
  checkExistingConsent() {
    try {
      // Check localStorage first
      const storedConsent = localStorage.getItem('gdpr-consent');
      if (storedConsent) {
        const consent = JSON.parse(storedConsent);
        this.consentTypes = { ...this.consentTypes, ...consent };
        this.consentGiven = true;
        this.loadConsentedScripts();
        return;
      }
      
      // Check cookies as fallback
      const cookieConsent = this.getCookie('gdpr-consent');
      if (cookieConsent) {
        const consent = JSON.parse(decodeURIComponent(cookieConsent));
        this.consentTypes = { ...this.consentTypes, ...consent };
        this.consentGiven = true;
        this.loadConsentedScripts();
        return;
      }
      
      // No consent found - only load necessary scripts
      this.loadNecessaryScripts();
      
    } catch (error) {
      console.error('Error checking consent:', error);
      this.loadNecessaryScripts();
    }
  }

  /**
   * Set up consent change listener
   */
  setupConsentListener() {
    // Listen for consent events
    document.addEventListener('gdpr-consent-updated', (event) => {
      this.handleConsentUpdate(event.detail);
    });
    
    // Listen for cookie banner interactions
    document.addEventListener('click', (event) => {
      const target = event.target;
      
      // Accept all button
      if (target.matches('[data-gdpr-accept-all]')) {
        this.giveFullConsent();
      }
      
      // Reject all button
      if (target.matches('[data-gdpr-reject-all]')) {
        this.rejectConsent();
      }
      
      // Custom consent button
      if (target.matches('[data-gdpr-consent]')) {
        const consentType = target.getAttribute('data-gdpr-consent');
        const allowed = target.checked || target.getAttribute('data-gdpr-allowed') === 'true';
        this.updateConsent(consentType, allowed);
      }
    });
  }

  /**
   * Set up deferred loading for user interaction
   */
  setupDeferredLoading() {
    // Load scripts on first user interaction (if consent given)
    const userEvents = ['click', 'scroll', 'keydown', 'touchstart'];
    
    const loadOnInteraction = () => {
      if (this.consentGiven) {
        this.loadConsentedScripts();
      }
      
      // Remove listeners after first interaction
      userEvents.forEach(event => {
        document.removeEventListener(event, loadOnInteraction, { passive: true });
      });
    };
    
    userEvents.forEach(event => {
      document.addEventListener(event, loadOnInteraction, { passive: true });
    });
    
    // Fallback: load after 5 seconds if no interaction
    setTimeout(() => {
      if (this.consentGiven) {
        this.loadConsentedScripts();
      }
    }, 5000);
  }

  /**
   * Handle consent update
   */
  handleConsentUpdate(consent) {
    this.consentTypes = { ...this.consentTypes, ...consent };
    this.consentGiven = true;
    this.saveConsent();
    this.loadConsentedScripts();
    
    if (this.debug) {
      console.log('🔒 Consent updated:', this.consentTypes);
    }
  }

  /**
   * Give full consent to all categories
   */
  giveFullConsent() {
    this.consentTypes = {
      analytics: true,
      marketing: true,
      functional: true,
      necessary: true
    };
    this.consentGiven = true;
    this.saveConsent();
    this.loadConsentedScripts();
    
    if (this.debug) {
      console.log('🔒 Full consent given');
    }
  }

  /**
   * Reject all non-necessary consent
   */
  rejectConsent() {
    this.consentTypes = {
      analytics: false,
      marketing: false,
      functional: false,
      necessary: true
    };
    this.consentGiven = true;
    this.saveConsent();
    this.loadNecessaryScripts();
    
    if (this.debug) {
      console.log('🔒 Consent rejected');
    }
  }

  /**
   * Update specific consent type
   */
  updateConsent(type, allowed) {
    if (this.consentTypes.hasOwnProperty(type)) {
      this.consentTypes[type] = allowed;
      this.consentGiven = true;
      this.saveConsent();
      
      if (allowed) {
        this.loadScriptsByType(type);
      }
    }
  }

  /**
   * Save consent to localStorage and cookie
   */
  saveConsent() {
    try {
      const consentData = JSON.stringify(this.consentTypes);
      
      // Save to localStorage
      localStorage.setItem('gdpr-consent', consentData);
      
      // Save to cookie as fallback
      const expiryDate = new Date();
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      document.cookie = `gdpr-consent=${encodeURIComponent(consentData)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
      
    } catch (error) {
      console.error('Error saving consent:', error);
    }
  }

  /**
   * Load scripts based on consent
   */
  loadConsentedScripts() {
    this.loadNecessaryScripts();
    
    if (this.consentTypes.analytics) {
      this.loadAnalyticsScripts();
    }
    
    if (this.consentTypes.marketing) {
      this.loadMarketingScripts();
    }
    
    if (this.consentTypes.functional) {
      this.loadFunctionalScripts();
    }
  }

  /**
   * Load necessary scripts (always loaded)
   */
  loadNecessaryScripts() {
    // Core functionality scripts
    this.loadScript('theme-core', 'necessary');
    this.loadScript('cart-functionality', 'necessary');
    this.loadScript('checkout-security', 'necessary');
  }

  /**
   * Load analytics scripts
   */
  loadAnalyticsScripts() {
    // Google Analytics
    if (!this.loadedScripts.has('google-analytics')) {
      this.loadGoogleAnalytics();
    }
    
    // Shopify Analytics
    this.loadScript('shopify-analytics', 'analytics');
    
    // Performance monitoring
    this.loadScript('performance-monitor', 'analytics');
  }

  /**
   * Load marketing scripts
   */
  loadMarketingScripts() {
    // Facebook Pixel
    if (!this.loadedScripts.has('facebook-pixel')) {
      this.loadFacebookPixel();
    }
    
    // Reviews.io
    this.loadScript('reviews-io', 'marketing');
    
    // Affiliate tracking
    this.loadScript('affiliate-tracking', 'marketing');
  }

  /**
   * Load functional scripts
   */
  loadFunctionalScripts() {
    // Chat widgets
    this.loadScript('chat-widget', 'functional');
    
    // Product recommendations
    this.loadScript('product-recommendations', 'functional');
    
    // Social media embeds
    this.loadScript('social-embeds', 'functional');
  }

  /**
   * Load scripts by consent type
   */
  loadScriptsByType(type) {
    switch (type) {
      case 'analytics':
        this.loadAnalyticsScripts();
        break;
      case 'marketing':
        this.loadMarketingScripts();
        break;
      case 'functional':
        this.loadFunctionalScripts();
        break;
    }
  }

  /**
   * Load Google Analytics with consent mode
   */
  loadGoogleAnalytics() {
    if (this.loadedScripts.has('google-analytics')) return;
    
    // Google Analytics 4 with consent mode
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    
    // Set default consent state
    gtag('consent', 'default', {
      'analytics_storage': this.consentTypes.analytics ? 'granted' : 'denied',
      'ad_storage': this.consentTypes.marketing ? 'granted' : 'denied'
    });
    
    gtag('js', new Date());
    gtag('config', 'GA_MEASUREMENT_ID'); // Replace with actual ID
    
    // Load GA script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=GA_MEASUREMENT_ID';
    document.head.appendChild(script);
    
    this.loadedScripts.add('google-analytics');
    
    if (this.debug) {
      console.log('🔒 Google Analytics loaded');
    }
  }

  /**
   * Load Facebook Pixel
   */
  loadFacebookPixel() {
    if (this.loadedScripts.has('facebook-pixel')) return;
    
    !function(f,b,e,v,n,t,s) {
      if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s);
    }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');
    
    fbq('init', 'FACEBOOK_PIXEL_ID'); // Replace with actual ID
    fbq('track', 'PageView');
    
    this.loadedScripts.add('facebook-pixel');
    
    if (this.debug) {
      console.log('🔒 Facebook Pixel loaded');
    }
  }

  /**
   * Generic script loader with consent checking
   */
  loadScript(name, consentType, src = null, callback = null) {
    // Check if already loaded
    if (this.loadedScripts.has(name)) {
      return;
    }
    
    // Check consent
    if (consentType !== 'necessary' && !this.consentTypes[consentType]) {
      this.queueScript(name, consentType, src, callback);
      return;
    }
    
    // Map script names to actual sources
    const scriptMap = {
      // 'performance-monitor': //carbethplants.co.uk/cdn/shop/t/109/assets/performance-monitor.js?v=102185875595982107551790847744,
      'reviews-io': 'https://widget.reviews.co.uk/carousel-inline-iframeless/dist.js',
      // 'theme-core': //carbethplants.co.uk/cdn/shop/t/109/assets/theme.js?v=88789134609738360051790847744,
      // Add more script mappings as needed
    };
    
    const scriptSrc = src || scriptMap[name];
    if (!scriptSrc) {
      console.warn(`Script source not found for: ${name}`);
      return;
    }
    
    // Load the script
    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      this.loadedScripts.add(name);
      if (callback) callback();
      
      if (this.debug) {
        console.log(`🔒 Script loaded: ${name} (${consentType})`);
      }
    };
    
    script.onerror = () => {
      console.error(`Failed to load script: ${name}`);
    };
    
    document.head.appendChild(script);
  }

  /**
   * Queue script for later loading
   */
  queueScript(name, consentType, src, callback) {
    this.scriptQueue.push({ name, consentType, src, callback });
    
    if (this.debug) {
      console.log(`🔒 Script queued: ${name} (${consentType})`);
    }
  }

  /**
   * Get cookie value
   */
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  /**
   * Check if consent is given for a specific type
   */
  hasConsent(type) {
    return this.consentTypes[type] || false;
  }

  /**
   * Get current consent state
   */
  getConsentState() {
    return { ...this.consentTypes };
  }

  /**
   * Revoke all consent and reload page
   */
  revokeConsent() {
    localStorage.removeItem('gdpr-consent');
    document.cookie = 'gdpr-consent=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    if (this.debug) {
      console.log('🔒 Consent revoked - page will reload');
    }
    
    // Reload page to reset state
    window.location.reload();
  }
}

// Initialize GDPR Lazy Loader
if (typeof window !== 'undefined') {
  window.gdprLazyLoader = new GDPRLazyLoader();
  
  // Add to global Eurus object if it exists
  if (window.Eurus) {
    window.Eurus.gdprLazyLoader = window.gdprLazyLoader;
  }
  
  // Expose consent methods globally for easy access
  window.giveGDPRConsent = (type, allowed) => {
    window.gdprLazyLoader.updateConsent(type, allowed);
  };
  
  window.checkGDPRConsent = (type) => {
    return window.gdprLazyLoader.hasConsent(type);
  };
}
